import React, { useState, useEffect } from 'react';
import { Button } from '@saleshandy/design-system';
import Switch, {
  Size as SwitchSize,
} from '../../../../../../../../../../shared/design-system/components/atoms/switch';
import SettingsText from '../settings-text';
import AudioPlayer from '../../../../../../../audio-player';
import RecordVoicemailPopup from '../../../../../../../record-voicemail-popup';
import GlobalRestrictionModal from '../../../../../../../../../home/components/global-restriction-modal';

export interface IVoicemailGreetingSettingsProps {
  isVoicemailGreetingEnabled: boolean;
  onToggle: (enabled: boolean) => void;
  isLoading?: boolean;
  audioSrc?: string | null;
  onSaveVoicemail?: (
    audioBlob: Blob,
    fileName: string,
    fileSize: number,
    duration: number,
  ) => Promise<void>;
  isDemoAccount?: boolean;
}

const VoicemailGreetingSettings: React.FC<IVoicemailGreetingSettingsProps> = ({
  isVoicemailGreetingEnabled: initialIsVoicemailGreetingEnabled,
  onToggle,
  isLoading = false,
  audioSrc,
  onSaveVoicemail,
  isDemoAccount,
}) => {
  const [isVoicemailGreetingEnabled, setIsVoicemailGreetingEnabled] = useState(
    initialIsVoicemailGreetingEnabled,
  );
  const [showRecordVoicemailPopup, setShowRecordVoicemailPopup] = useState(
    false,
  );
  const [showGlobalRestrictionModal, setShowGlobalRestrictionModal] = useState(
    false,
  );
  // Sync local state with prop value when it changes
  useEffect(() => {
    setIsVoicemailGreetingEnabled(initialIsVoicemailGreetingEnabled);
  }, [initialIsVoicemailGreetingEnabled]);

  const handleChange = (checked: boolean) => {
    setIsVoicemailGreetingEnabled(checked);
    onToggle(checked);
  };

  const handleChangeGreetingClick = () => {
    if (isDemoAccount) {
      setShowGlobalRestrictionModal(true);
      return;
    }
    setShowRecordVoicemailPopup(true);
  };

  const handleCloseRecordVoicemailPopup = () => {
    setShowRecordVoicemailPopup(false);
  };

  return (
    <div
      id="voicemail-greeting"
      className="settings-wrapper voicemail-greeting-settings"
    >
      <SettingsText
        title="Voicemail Greeting"
        description="Send voicemail greeting when the incoming call is missed"
      />
      <div className="enable-voicemail-greeting">
        <Switch
          checked={isVoicemailGreetingEnabled}
          onChange={handleChange}
          size={SwitchSize.Small}
          disabled={isLoading}
        />
        <AudioPlayer
          audioSrc={audioSrc}
          className={`voicemail-greeting-audio-player ${
            isLoading ? 'audio-player-loading' : ''
          }`}
        />
        <Button
          variant="secondary"
          onClick={handleChangeGreetingClick}
          className="change-greeting-btn"
        >
          Change Greeting
        </Button>
      </div>
      {showRecordVoicemailPopup && (
        <RecordVoicemailPopup
          show={showRecordVoicemailPopup}
          onClose={handleCloseRecordVoicemailPopup}
          onSave={onSaveVoicemail}
          fromSettings={true}
          hideLanguageSelection={true}
        />
      )}
      <GlobalRestrictionModal
        show={showGlobalRestrictionModal}
        onClose={() => setShowGlobalRestrictionModal(false)}
        title="Changing voicemail greeting is disabled in demo mode"
        content="This is a demo account with sample data. Signup to change voicemail greeting"
        primaryBtnLabel="Sign up"
      />
    </div>
  );
};

export default VoicemailGreetingSettings;
